import { useState, useEffect } from "react";
import { Stack, H4, Title, useToast } from "@introist/react-foundation/v2";
import { FormDetailsSettings } from "./FormDetailsSettings";
import { BrandingSettings } from "./BrandingSettings";
import { UnsavedChanges } from "components/molecules";
import { useFormActions } from "../../useForms";

interface FormBranding {
  backgroundColor?: string;
  primaryColor?: string;
  logoUrl?: string;
  backgroundImageUrl?: string;
  backgroundImageMode?: "full" | "banner";
  fontFamily?: string | null;
}

interface WelcomeScreen {
  title: string;
  description: string;
}

interface FormPreviewDrawerProps {
  drawerOpen: boolean;
  formId: string;
  form: any;
  onBrandingChange: (branding: FormBranding) => void;
  onWelcomeScreenChange: (welcomeScreen: WelcomeScreen) => void;
  onSaveComplete: () => void;
}

export const FormPreviewDrawer = ({
  drawerOpen,
  formId,
  form,
  onBrandingChange,
  onWelcomeScreenChange,
  onSaveComplete
}: FormPreviewDrawerProps) => {
  const toast = useToast();
  const { update } = useFormActions();

  // Reset key to force HtmlEditor remount on discard
  const [resetKey, setResetKey] = useState(0);

  // Current state
  const [branding, setBranding] = useState<FormBranding>(
    form?.branding || {
      backgroundColor: undefined,
      primaryColor: undefined,
      logoUrl: undefined,
      backgroundImageUrl: undefined,
      backgroundImageMode: "full",
      fontFamily: null
    }
  );
  // Initialize welcomeScreen from form.welcomeScreen or fallback to form title/description
  const [welcomeScreen, setWelcomeScreen] = useState<WelcomeScreen>({
    title: form?.welcomeScreen?.title ?? (form?.title || ""),
    description: form?.welcomeScreen?.description ?? (form?.description || "")
  });

  // Original state (for comparison and discard)
  const [originalBranding, setOriginalBranding] = useState<FormBranding>(
    form?.branding || {
      backgroundColor: undefined,
      primaryColor: undefined,
      logoUrl: undefined,
      backgroundImageUrl: undefined,
      backgroundImageMode: "full",
      fontFamily: null
    }
  );
  const [originalWelcomeScreen, setOriginalWelcomeScreen] = useState<WelcomeScreen>({
    title: form?.welcomeScreen?.title ?? (form?.title || ""),
    description: form?.welcomeScreen?.description ?? (form?.description || "")
  });

  // Initialize when form data loads
  useEffect(() => {
    if (form) {
      setBranding(
        form.branding || {
          backgroundColor: undefined,
          primaryColor: undefined,
          logoUrl: undefined,
          backgroundImageUrl: undefined,
          backgroundImageMode: "full",
          fontFamily: null
        }
      );
      setWelcomeScreen({
        title: form.welcomeScreen?.title ?? (form.title || ""),
        description: form.welcomeScreen?.description ?? (form.description || "")
      });
      setOriginalBranding(
        form.branding || {
          backgroundColor: undefined,
          primaryColor: undefined,
          logoUrl: undefined,
          backgroundImageUrl: undefined,
          backgroundImageMode: "full",
          fontFamily: null
        }
      );
      setOriginalWelcomeScreen({
        title: form.welcomeScreen?.title ?? (form.title || ""),
        description: form.welcomeScreen?.description ?? (form.description || "")
      });
      setResetKey(prev => prev + 1); // Force remount with new form data
    }
  }, [form]);

  // Check for unsaved changes
  const hasUnsavedChanges =
    JSON.stringify(branding) !== JSON.stringify(originalBranding) ||
    JSON.stringify(welcomeScreen) !== JSON.stringify(originalWelcomeScreen);

  // Handle welcome screen change
  const handleWelcomeScreenChange = (newTitle: string, newDescription: string) => {
    const newWelcomeScreen = { title: newTitle, description: newDescription };
    setWelcomeScreen(newWelcomeScreen);
    onWelcomeScreenChange(newWelcomeScreen);
  };

  // Handle branding change
  const handleBrandingChange = (newBranding: FormBranding) => {
    setBranding(newBranding);
    onBrandingChange(newBranding);
  };

  // Unified save handler - single API call
  const handleSaveAll = async () => {
    try {
      await update({
        id: formId,
        update: {
          branding,
          welcomeScreen
        }
      });

      // Update original state after successful save
      setOriginalBranding(branding);
      setOriginalWelcomeScreen(welcomeScreen);

      toast.success("Changes saved successfully");
      onSaveComplete();
    } catch (error) {
      toast.error("Failed to save changes");
      throw error;
    }
  };

  // Unified discard handler
  const handleDiscardAll = () => {
    setBranding(originalBranding);
    setWelcomeScreen(originalWelcomeScreen);
    onBrandingChange(originalBranding);
    onWelcomeScreenChange(originalWelcomeScreen);
    setResetKey(prev => prev + 1); // Force HtmlEditor remount
  };

  return (
    <div
      style={{
        width: drawerOpen ? "500px" : "0px",
        height: "100vh",
        overflow: drawerOpen ? "auto" : "hidden",
        transition: "width 0.3s ease",
        borderLeft: drawerOpen ? "1px solid var(--border-color, #e0e0e0)" : "none",
        backgroundColor: "var(--background-color, #ffffff)"
      }}
    >
      {drawerOpen && (
        <Stack vertical gap="large" style={{ padding: "var(--spacing-xLarge)" }}>
          {/* Header */}
          <Stack vertical gap="small">
            <H4>Form Settings</H4>
            <Title>Customize your form's appearance and content</Title>
          </Stack>

          {/* Welcome Screen Settings */}
          <FormDetailsSettings
            key={resetKey}
            title={welcomeScreen.title}
            description={welcomeScreen.description}
            onChange={handleWelcomeScreenChange}
          />

          {/* Branding Settings */}
          <BrandingSettings branding={branding} onChange={handleBrandingChange} />
        </Stack>
      )}

      {/* Unsaved Changes Banner */}
      {drawerOpen && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "0 var(--spacing-large)",
            position: "sticky",
            bottom: "1rem"
          }}
        >
          <UnsavedChanges
            show={hasUnsavedChanges}
            onSave={handleSaveAll}
            onDiscard={handleDiscardAll}
            style={{
              width: "350px"
            }}
          />
        </div>
      )}
    </div>
  );
};
